import { BadgeCheck, Cloud, GalleryHorizontalEnd, HardDrive, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { IntelligenceWidgetGrid } from "../../components/dashboard/IntelligenceWidgetGrid";
import { getActivity, getAdminClients, getAdminDashboardIntelligence } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { ActivityItem, BusinessCenterIntelligencePayload, ClientRecord } from "../../lib/types";

export function AdminDashboardPage() {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [intelligence, setIntelligence] = useState<BusinessCenterIntelligencePayload | null>(null);

  useEffect(() => {
    void getAdminClients().then(setClients);
    void getActivity().then(setActivity);
    void getAdminDashboardIntelligence().then(setIntelligence);
  }, []);

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Business Center OS Intelligence"
        title="All platform health, recommendations, and marketplace signals in one command center."
        subtitle="Use the admin dashboard to surface business health, AI recommendations, marketplace opportunities, and operational status across the platform."
      />
      {intelligence ? <IntelligenceWidgetGrid payload={intelligence} mode="admin" /> : null}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <AdminStat icon={Users} label="Client Vaults" value={clients.length} />
        <AdminStat icon={GalleryHorizontalEnd} label="Active Events" value={clients.length + 2} />
        <AdminStat icon={BadgeCheck} label="Setup Connected" value={clients.filter((c) => c.storageSetupStatus === "connected").length} />
        <AdminStat icon={HardDrive} label="Storage Follow-up" value={clients.filter((c) => c.storageSetupStatus !== "connected").length} />
      </section>
      <section className="grid gap-6 lg:grid-cols-[1fr_.8fr]">
        <article className="ivory-card rounded-lg p-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-display text-3xl font-bold">Client Vaults</h2>
            <Link to="/admin/clients" className="focus-ring rounded-full bg-ktd-black px-4 py-2 text-sm font-extrabold text-ktd-softGold">
              View Clients
            </Link>
          </div>
          <div className="mt-5 grid gap-3">
            {clients.map((client) => (
              <Link key={client.id} to={`/admin/client/${client.id}`} className="rounded-lg bg-white p-4 transition hover:shadow-gold">
                <p className="font-extrabold text-ktd-rich">{client.clientName}</p>
                <p className="mt-1 text-sm text-ktd-muted">{client.clientEmail}</p>
                <span className="status-pill status-featured mt-3">{client.storageSetupStatus.replaceAll("_", " ")}</span>
              </Link>
            ))}
          </div>
        </article>
        <article className="rounded-lg bg-ktd-black p-6 text-white shadow-luxe">
          <Cloud className="size-8 text-ktd-softGold" />
          <h2 className="mt-5 font-display text-3xl font-bold">Cloudflare Setup Instructions</h2>
          <p className="mt-4 text-sm leading-6 text-white/66">
            Confirm the client-owned account, payment method, R2 bucket, D1 database, Turnstile widget, and collaborator
            invite before launching guest uploads.
          </p>
          <Link
            to="/admin/storage-setup"
            className="focus-ring mt-6 inline-flex rounded-full bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black"
          >
            View Storage Setup
          </Link>
        </article>
      </section>
      <section className="ivory-card rounded-lg p-6">
        <h2 className="font-display text-3xl font-bold">Activity Log</h2>
        <div className="mt-5 grid gap-3">
          {activity.map((item) => (
            <div key={item.id} className="rounded-lg bg-white p-4">
              <p className="text-sm font-extrabold text-ktd-rich">{item.details}</p>
              <p className="mt-1 text-xs font-semibold text-ktd-muted">{item.actorEmail || "System"} - {formatDate(item.createdAt)}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function AdminStat({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number }) {
  return (
    <article className="ivory-card rounded-lg p-5">
      <Icon className="size-7 text-[#D94A16]" />
      <p className="mt-4 text-sm font-bold text-ktd-muted">{label}</p>
      <strong className="mt-2 block font-display text-4xl">{value}</strong>
    </article>
  );
}
